import { viridis } from "../../../../utils/viridisColorScale";
import { getContrastColorFromRgba } from "../../../../utils/contrastColor";
import { breaks, generalStatistics } from "./utilities";

export function legendObservationsCount(counts = []) {
  console.log("Generating observation count legend and representations");
  let defaultRanges = [
    {
      text: "1 Observation",
      symbol: { type: "fa fa-circle", size: 10, text: null },
      min: 1,
      max: 1,
      color: getContrastColorFromRgba(viridis(0)),
      backgroundColor: viridis(0),
    },
    {
      text: "2",
      symbol: { type: "fa fa-plus", size: 10, text: null },
      min: 2,
      max: 2,
      color: getContrastColorFromRgba(viridis(50)),
      backgroundColor: viridis(50),
    },
    {
      text: "3",
      symbol: { type: "fa fa-plus", size: 10, text: null },
      min: 3,
      max: 3,
      color: getContrastColorFromRgba(viridis(75)),
      backgroundColor: viridis(75),
    },
    {
      text: "4",
      symbol: { type: "fa fa-plus", size: 10, text: null },
      min: 4,
      max: 4,
      color: getContrastColorFromRgba(viridis(100)),
      backgroundColor: viridis(100),
    },
  ];

  // if there are no counts, return default legend
  if (!counts || counts.length === 0) {
    let defaultLegend = {
      scale: null, // equal_interval, equal_count, natural_breaks
      min: 0,
      max: 0,
      wholeNumber: true,
      maxCharacters: 0,
      ranges: [{ ...defaultRanges[0], locationCount: 0 }],
    };
    return defaultLegend;
  }

  // Process counts array to create legend and representation
  const maxCount = Math.max(...counts);
  const minCount = Math.min(...counts);
  const wholeNumber = true;
  const maxCharacters = Math.max(...counts.map((c) => String(c).length));

  // handle case where max observations across locations is 1, 2, 3, or 4
  if (maxCount <= 4) {
    let simpleRanges = defaultRanges.slice(0, maxCount + 1);

    // Count locations for each range
    const rangeCounts = new Array(simpleRanges.length).fill(0);
    counts.forEach((count) => {
      if (count >= 0 && count <= maxCount) {
        rangeCounts[count]++;
      }
    });

    // Add locationCount to each range
    simpleRanges = simpleRanges.map((range, index) => ({
      ...range,
      locationCount: rangeCounts[index],
    }));

    let simpleLegend = {
      scale: "equal_interval", // equal_interval, equal_count, natural_breaks
      min: minCount,
      max: maxCount,
      wholeNumber: true,
      maxCharacters: maxCharacters,
      ranges: simpleRanges,
    };
    return simpleLegend;
  }

  // Calculate jenks natural breaks for counts greater than 1
  // using 3 jenks classes
  // no observations and 1 observation is a special case
  // (total of 5 cases)
  const numClasses = 4;
  const breakPoints = breaks.jenksNatural(
    counts.filter((c) => c > 1),
    numClasses,
  );

  // e.g. breaks = [5, 15, 30] => ranges = [5-14, 15-30]
  const breakRanges = [];
  for (let i = 0; i < breakPoints.length - 1; i++) {
    const min = breakPoints[i];
    // Don't subtract 1 from the last break point to include the maximum value
    const max =
      i === breakPoints.length - 2
        ? breakPoints[i + 1]
        : breakPoints[i + 1] - 1;
    breakRanges.push({ min: min, max: max });
  }
  const ranges = [];
  breakRanges.forEach((range, index) => {
    range.text =
      range.min === range.max ? `${range.min}` : `${range.min} - ${range.max}`;
    range.symbol = {
      type: "fa fa-plus",
      size: 10,
      text: null,
    };
    // colorScaleValue should range from 20 to 100 over the breakRanges
    // this ensures they all have darker colors for better visibility
    const colorScaleValue = 10 + (index + 1) * (90 / breakRanges.length);
    const color = viridis(colorScaleValue);
    range.backgroundColor = color;
    range.color = getContrastColorFromRgba(color);
    ranges.push(range);
  });

  ranges.unshift(defaultRanges[0]); // add 1 observation range

  // Create counts array for each range, then loop through counts once
  const rangeCounts = new Array(ranges.length).fill(0);

  counts.forEach((count) => {
    if (count === 0) {
      rangeCounts[0]++; // "1 observation" is first
    } else {
      // For counts > 1, find the matching breakRange (indices 1 to length-2)
      for (let i = 1; i < ranges.length - 1; i++) {
        if (count >= ranges[i].min && count <= ranges[i].max) {
          rangeCounts[i]++;
          break;
        }
      }
    }
  });

  const rangeWithCounts = ranges.map((range, index) => ({
    ...range,
    locationCount: rangeCounts[index],
  }));

  const legend = {
    scale: "natural_breaks",
    min: minCount,
    max: maxCount,
    wholeNumber: wholeNumber,
    maxCharacters: maxCharacters,
    ranges: rangeWithCounts,
  };

  return legend;
}
